export interface IProfileSettingsForm {
  profileSettingPopUpActive: boolean;
  checked: {
    showFineGrid: boolean;
    previews: boolean;
  };
  input: {
    fineGrid: number;
    sampleInterval: number;
    verticalInterval: number;
  };
}

export const profileSettingsFormDefault: IProfileSettingsForm = {
  profileSettingPopUpActive: false,
  checked: {
    showFineGrid: false,
    previews: false,
  },
  input: {
    fineGrid: Math.trunc(25.0),
    sampleInterval: 2.0,
    verticalInterval: 5.0,
  },
};

export interface IReachTableCol {
  name: string;
  title: string;
  editable: boolean;
  editableOnStartAndEnd?: boolean;
  editableOnR0?: boolean;
  trapezoidalOnly?: boolean;
  parabolicOnly?: boolean;
  blankOnR0?: boolean;
  blankOnStartAndEnd?: boolean;
}

const lengthAndSlope: IReachTableCol[] = [
  { name: 'stationDist', title: 'Length\n(ft)', editable: false, editableOnStartAndEnd: true, blankOnR0: true },
  { name: 'grade', title: 'Slope\n(%)', editable: false, editableOnStartAndEnd: true, blankOnR0: true },
];

const drainAndBlock: IReachTableCol[] = [
  { name: 'draiBblock', title: 'Drain/Block', editable: false, editableOnStartAndEnd: true, blankOnR0: true },
];

export const stationViewBeforeCols: IReachTableCol[] = [
  { name: 'endStation', title: 'Station\n(ft)', editable: true, editableOnR0: true, blankOnStartAndEnd: true },
  { name: 'groundElev', title: 'Ground Z\n(ft)', editable: false },
  { name: 'channelElev', title: 'Channel Z\n(ft)', editable: true, editableOnR0: true },
  { name: 'cutFill', title: 'Cut/ Fill\n(ft)', editable: false },
];

export const stationViewAfterCols: IReachTableCol[] = [
  { name: 'runoffQ', title: 'Runoff Q\n(cfs)', editable: false, blankOnStartAndEnd: true },
  { name: 'addedQ', title: 'Added Q\n(cfs)', editable: true, editableOnR0: true, blankOnStartAndEnd: true },
  { name: 'totalQ', title: 'Total Q\n(cfs)', editable: false, blankOnStartAndEnd: true },
];

const reachViewBeforeCols: IReachTableCol[] = [
  { name: 'reachId', title: 'Reach', editable: false },
  { name: 'startStation', title: 'Start\n(ft)', editable: false },
  { name: 'endStation', title: 'End\n(ft)', editable: false },
];

const flowAndHeight = [
  { name: 'flowQ', title: 'Flow Q\n(cfs)', editable: false, editableOnR0: false, blankOnStartAndEnd: false },
  { name: 'flowVelocity', title: 'Flow Velocity\n(ft/sec)', editable: false, editableOnR0: false, blankOnStartAndEnd: false },
  { name: 'peakFlowDepth', title: 'Flow Depth\n(ft)', editable: false, editableOnR0: false, blankOnStartAndEnd: false },
  { name: 'designHeight', title: 'Design Height\n(ft)', editable: false, editableOnR0: false, blankOnStartAndEnd: false },
];

const templateViewCols: IReachTableCol[] = [
  { name: 'templateName', title: 'Template', editable: false },
  { name: 'templateParams.cutSlope', title: 'Cut Slope', editable: false },
  { name: 'templateParams.frontSlope', title: 'Front Slope', editable: false },
  { name: 'templateParams.backSlope', title: 'Back Slope', editable: false },
  { name: 'templateParams.bottomWidth', title: 'Bottom Width\n(ft)', editable: false },
  { name: 'templateParams.frontHeight', title: 'Front Height\n(ft)', editable: false },
  { name: 'templateParams.topWidth', title: 'Top Width\n(ft)', editable: false },
];

// *** Combined View ***
export const combinedViewCols: IReachTableCol[] = [
  ...reachViewBeforeCols,
  ...stationViewBeforeCols,
  ...lengthAndSlope,
  ...templateViewCols,
  ...flowAndHeight,
  ...drainAndBlock,
];

// *** Reach View ***
export const reachViewCols: IReachTableCol[] = [...reachViewBeforeCols, ...lengthAndSlope, ...flowAndHeight];

// *** Station View ***
export const stationViewCols: IReachTableCol[] = [
  ...stationViewBeforeCols,
  ...lengthAndSlope,
  ...flowAndHeight,
  ...drainAndBlock,
];

export const reachTableTabs = [
  { tab: 'combined-view', title: 'Combined View', defaultCols: combinedViewCols },
  { tab: 'station-view', title: 'Station View', defaultCols: stationViewCols },
  { tab: 'reach-view', title: 'Reach View', defaultCols: reachViewCols },
  { tab: 'template-view', title: 'Template View', defaultCols: templateViewCols },
];
export const initialFormValidationTerraceDesign = {
  formErrors: {
    reachId: '',
    status: '',
    startStation: '',
    endStation: '',
    cutFill: '',
    groundElev: '',
    channelElev: '',
    stationDist: '',
    grade: '',
    flowQ: '',
    flowVelocity: '',
    peakFlowDepth: '',
    frontHeight: '',
    bottomWidth: '',
    estimatedCutSlopeRatio: '',
    simWidth: '',
    designHeight: '',
    liningMaterial: '',
    templateName: '',
    templateParams: {
      slopeMode: '',
      cutSlope: '',
      frontSlope: '',
      backSlope: '',
      bottomWidth: '',
      frontHeight: '',
      topWidth: '',
    },
    drainStationDto: {
      drainName: '',
      drainFraction: '',
      drainOffset: '',
      ugoAndInlet: '',
      location: {
        pointName: '',
        description: '',
      },
    },
    blockStationDto: {
      blockHeight: '',
    },
  },
  reachIdValid: true,
  statusValid: true,
  startStationValid: true,
  endStationValid: true,
  cutFillValid: true,
  groundElevValid: true,
  channelElevValid: true,
  stationDistValid: true,
  gradeValid: true,
  flowQValid: true,
  flowVelocityValid: true,
  peakFlowDepthValid: true,
  frontHeightValid: true,
  bottomWidthValid: true,
  estimatedCutSlopeRatioValid: true,
  simWidthValid: true,
  designHeightValid: true,
  liningMaterialValid: true,
  templateNameValid: true,
  templateParamsValid: {
    slopeModeValid: true,
    cutSlopeValid: true,
    frontSlopeValid: true,
    backSlopeValid: true,
    bottomWidthValid: true,
    frontHeightValid: true,
    topWidthValid: true,
  },
  drainStationDtoValid: {
    drainNameValid: true,
    drainFractionValid: true,
    drainOffsetValid: true,
    ugoAndInletValid: true,
    locationValid: {
      pointNameValid: true,
      descriptionValid: true,
    },
  },
  blockStationDtoValid: {
    blockHeightValid: true,
  },
  formValid: true,
  invalidFields: [] as { fieldName: string; index: number }[],
};